import { useState } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Plus, ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import RichTextEditor from "../admin/RichTextEditor";

const uid = () => Math.random().toString(36).slice(2, 12);

function Row({ faq, index, count, onChange, onRemove, onMove, expanded, onToggleExpand }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: faq.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="rounded-lg border border-white/10 bg-white/5" data-testid={`faq-row-${index}`}>
      <div className="flex items-center gap-2 p-2">
        <button type="button" className="cursor-grab text-white/40 hover:text-white touch-none" {...attributes} {...listeners} aria-label="Drag to reorder"><GripVertical className="h-4 w-4" /></button>
        <div className="flex flex-col">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} aria-label="Move up" className="text-white/40 hover:text-white disabled:opacity-20"><ChevronUp className="h-3 w-3" /></button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === count - 1} aria-label="Move down" className="text-white/40 hover:text-white disabled:opacity-20"><ChevronDown className="h-3 w-3" /></button>
        </div>
        <Input value={faq.question} onChange={(e) => onChange({ ...faq, question: e.target.value })} placeholder="Question" className="flex-1 bg-black/30 border-white/15" data-testid={`faq-question-input-${index}`} />
        <button type="button" onClick={() => onChange({ ...faq, enabled: !faq.enabled })} title={faq.enabled ? "Enabled (visible)" : "Disabled (hidden)"} className={faq.enabled ? "text-green-400" : "text-white/30"} data-testid={`faq-enable-${index}`}>
          {faq.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
        <button type="button" onClick={() => onToggleExpand(faq.id)} aria-expanded={expanded} className="text-white/50 hover:text-white" data-testid={`faq-expand-${index}`}>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-300" aria-label="Remove FAQ" data-testid={`faq-remove-${index}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      {expanded && (
        <div className="p-3 pt-0">
          <RichTextEditor value={faq.answer} onChange={(v) => onChange({ ...faq, answer: v })} minimal />
        </div>
      )}
    </div>
  );
}

export default function FaqManager({ value = [], onChange }) {
  const faqs = [...value].sort((a, b) => (a.order || 0) - (b.order || 0));
  const [expanded, setExpanded] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const commit = (list) => onChange(list.map((f, i) => ({ ...f, order: i })));
  const add = () => { const f = { id: uid(), question: "", answer: "", order: faqs.length, enabled: true }; commit([...faqs, f]); setExpanded(f.id); };
  const update = (i, f) => commit(faqs.map((x, j) => (j === i ? f : x)));
  const remove = (i) => commit(faqs.filter((_, j) => j !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= faqs.length) return; commit(arrayMove(faqs, i, j)); };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = faqs.findIndex((f) => f.id === active.id);
    const newI = faqs.findIndex((f) => f.id === over.id);
    commit(arrayMove(faqs, oldI, newI));
  };

  return (
    <div className="space-y-3" data-testid="faq-manager">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={faqs.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <Row key={f.id} faq={f} index={i} count={faqs.length} expanded={expanded === f.id}
                onToggleExpand={(id) => setExpanded(expanded === id ? null : id)}
                onChange={(nf) => update(i, nf)} onRemove={() => remove(i)} onMove={move} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {faqs.length === 0 && <p className="text-sm text-white/40">No FAQs yet. Add question & answer pairs to build an FAQ accordion and FAQPage structured data.</p>}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add} data-testid="faq-add"><Plus className="h-4 w-4" /> Add FAQ</Button>
    </div>
  );
}
